<template>
  <div>
    <v-container>
      <Breadcrumbs :breadcrumbs="breadcrumbs"/>
    </v-container>
    <v-container>
      <h1>Upload Employees</h1>
      <small>v 0.0.2</small>
      <v-alert v-if="showSuccessMsg" type="success" dismissible>
        <v-row v-if="createdEmpMsg">
          <v-col>{{ createdEmpMsg }}</v-col>
        </v-row>
        <v-row v-if="updatedEmpMsg">
          <v-col>{{ updatedEmpMsg }}</v-col>
        </v-row>
        <v-row v-if="removedEmpMsg">
          <v-col>{{ removedEmpMsg }}</v-col>
        </v-row>
        <v-row v-if="noChangesMsg">
          <v-col>{{ noChangesMsg }}</v-col>
        </v-row>
      </v-alert>
      <v-file-input
        v-model="file"
        chips
        accept=".csv" 
        label="Choose File"
      ></v-file-input>
      <v-btn 
        @click="upload"
        rounded
        :disabled="disableUploadButton"
        :loading="loadingUploadButton"
      >Upload</v-btn>
      <v-simple-table v-if="updateLog.length > 0">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">Name</th>
              <th class="text-left">Updates</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="update in updateLog" :key="update.name">
              <td>{{ update.name }}</td>
              <td>
                <ul style="list-style-type:none;">
                  <li v-for="field in update.updates" :key="field.field">
                    <strong>{{ field.field }}</strong><br/>
                    <v-responsive
                      class="text-center text-decoration-line-through red--text text--lighten-1 rounded-pill d-inline-flex align-center justify-center pa-1"
                    >
                      {{ field.oldVal }}
                    </v-responsive>
                    <v-responsive
                      class="text-center green--text rounded-pill d-inline-flex align-center justify-center pa-1"
                    >
                      {{ field.newVal }}
                    </v-responsive>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-container>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import moment from 'moment'
  import request from 'request'
  import csv from 'csvtojson'
  import Breadcrumbs from '@/components/Breadcrumbs'
  
  export default {
    name: 'UploadEmployees',
    components: {
      Breadcrumbs
    },
    data () {
      return {
        updateLog: [],
        breadcrumbs: [
          {
            text: 'Directory',
            disabled: false,
            to: { name: 'Employee Directory' }
          },
          {
            text: 'Upload Employees',
            disabled: true
          }
        ],
        createdEmpMsg: null,
        updatedEmpMsg: null,
        removedEmpMsg: null,
        noChangesMsg: null,
        showSuccessMsg: false,
        loadingUploadButton: false,
        file: null,
        csvHeaders: [
          { header: 'external_uuid',   airtableColumn: 'Position ID' },
          { header: 'gender',          airtableColumn: 'Gender', optional: true },
          { header: 'hire_date',       airtableColumn: 'Hire Date' },
          { header: 'birthday',        airtableColumn: 'Birthday'},
          { header: 'location',        airtableColumn: 'Location' },
          { header: 'manager_login',   airtableColumn: 'Manager Email' },
          { header: 'user_department', airtableColumn: 'Department' },
          { header: 'user_login',      airtableColumn: 'Email' },
          { header: 'user_name',       airtableColumn: 'Legal Name' },
          { header: 'user_title',      airtableColumn: 'Title' },
          { header: 'city_residence',  airtableColumn: 'City Residence', optional: true },
          { header: 'state_residence', airtableColumn: 'State Residence', optional: true},
          { header: 'race_ethnicity',  
            airtableColumn: 'Race / Ethnicity', 
            optional: true 
          },
          { header: 'preferred_name',  
            airtableColumn: 'Preferred Name', 
            optional: true 
          },
          { header: 'manager_position',  
            airtableColumn: 'Manager Position', 
            optional: true 
          },
          {
            header: 'job_code',
            airtableColumn: 'Job Code',
            optional: true
          }
        ]
      }
    },
    created () {
      this.$store.dispatch('getRecords', {
        table: 'Employees',
        view: 'All Employees',
        dispatch: 'setEmployees'
      })
    },
    computed: {
      disableUploadButton() {
        return this.employees.length === 0
               || !this.file
               || this.loadingUploadButton
      },
      employees () {
        return this.$store.getters.getEmployees
      }
    },
    methods: {
      createEmployees (employeeRecords) {
        return new Promise((resolve, reject) => {
          this.$store.dispatch('createRecords', {
            table: 'Employees',
            records: employeeRecords
          })
          .then((values) => {
            return resolve(values)
          })
          .catch((err) => {
            return reject(err)
          })
        })
      },
      updateEmployees (employeeRecords) {
        return new Promise((resolve, reject) => {
          this.$store.dispatch('updateRecords', {
            table: 'Employees',
            records: employeeRecords
          })
          .then((values) => {
            return resolve(values)
          })
          .catch((err) => {
            return reject(err)
          })
        })
      },
      async upload () {
        this.loadingUploadButton = true
        const that = this // we lose context in our functions
        const employees = this.employees
        const csvHeaders = this.csvHeaders
        let updatedEmployees = []
        let updates = []
        const createdEmployees = []
        const allEmployeesIdsInFile = []
        const storageRef = firebase.storage().ref()
        await storageRef.child('accounts.csv').put(this.file)
        const downloadUrl = await storageRef.child('accounts.csv')
                                  .getDownloadURL()
        csv()
        .fromStream(request.get(downloadUrl))
        .subscribe((json) => {
          return new Promise((resolve, reject) => {
            if (json && hasRequiredProperties(json)) {
              allEmployeesIdsInFile.push(json.external_uuid)
              const existingEmp = employees.find(e => {
                return e.fields['UUID'] === json.external_uuid
                // when we get a new position ID, change this to ['Email']
                // and run the upload so that we don't depend on position ID
                // for that specific upload only
              })
              if (existingEmp && hasUpdatedFields(json, existingEmp)) {
                if (!existingEmp['Ignore In Upload']) {
                  updatedEmployees.push(
                    mapEmployeeFieldsForUpdate(json, existingEmp.id)
                  )
                }
              } else if (!existingEmp) {
                createdEmployees.push(mapEmployeeFieldsForCreate(json))
              }
              return resolve(json)
            } else {
              return reject({
                success: false,
                message: 'Missing required fields',
                body: json
              })
            }
          })
        }, onError, onComplete)
        function onError(error) {
          console.log('error', error)
        }
        function onComplete() {
          let inactiveEmployees = []
          for (const e of that.employees) {
            if (!e.fields['Ignore In Upload']) {
              if (e.fields['Position ID'] && 
                  !allEmployeesIdsInFile.includes(e.fields['Position ID'])) {
                inactiveEmployees.push({
                  id: e.id,
                  fields: { Status: 'Inactive' }
                })
                logUpdates({},e,'Inactivated')
              }
            }
          }
          that.updateLog = updates
          if (inactiveEmployees.length > 0) {
            that.updateEmployees(inactiveEmployees)
            .then(() => {
              that.removedEmpMsg = `Removed ${inactiveEmployees.length} employees`
              that.showSuccessMsg = true
            })
          }
          if (updatedEmployees.length > 0) {
            that.updateEmployees(updatedEmployees)
            .then(() => {
              that.updatedEmpMsg = `Updated ${updatedEmployees.length} employees`
              that.showSuccessMsg = true
            })
          }
          if (createdEmployees.length > 0) {
            that.createEmployees(createdEmployees)
            .then(() => {
              that.createdEmpMsg = `Created ${createdEmployees.length} employees`
              that.showSuccessMsg = true
            })
          }
          if (updatedEmployees.length === 0 && createdEmployees.length === 0) {
            that.noChangesMsg = 'There are no changes to be uploaded'
            that.showSuccessMsg = true
          }
          that.file = null
          that.loadingUploadButton = false
        }
        function hasRequiredProperties (obj) {
          const requiredProps = csvHeaders.filter(h => !h.optional)
          for (const p of requiredProps) {
            if (!Object.prototype.hasOwnProperty.call(obj, p.header)) {
              return false
            }
          }
          return true
        }
        function hasUpdatedFields (obj, employee) {
          // existing inactive employee in the upload is now active
          if (employee.fields.Status === 'Inactive') {
            logUpdates(obj, employee)
            return true
          }
          for (const h of csvHeaders) {
            if (h.header === 'hire_date' || h.header === 'birthday') {
              // the hire and birthday date fields are formatted differently
              const date1 = moment(obj[h.header])
                            .format('YYYY-MM-DD')
              const date2 = moment(employee.fields[h.airtableColumn])
                            .format('YYYY-MM-DD')
              if (date1 !== date2) {
                logUpdates(obj, employee)
                return true
              }
            } else if (obj[h.header] !== employee.fields[h.airtableColumn]) {
              if (h.header === 'race_ethnicity' || h.header === 'city_residence' || h.header === 'state_residence' || h.header === 'manager_position') {
                logUpdates(obj, employee)
                return true
              } else if (employee.fields[h.airtableColumn] === undefined) {
                // do nothing if airtable is missing data for any other field
              } else {
                logUpdates(obj, employee)
                return true
              }
            }
          }
          return false
        }
        // TODO: this duplicates a lot of code from hasUpdatedFields
        function logUpdates (newVal, oldVal, status) {
          let newUpdates = []
          if (oldVal.fields.Staus === 'Inactive') {
            newUpdates.push({
              field: 'Status',
              oldVal: 'Inactive',
              newVal: 'Active'
            })
          }
          if (status === 'Inactivated') {
            newUpdates.push({
              field: 'Status',
              oldVal: 'Active',
              newVal: 'Inactive'
            })
            return updates.push({
              name: oldVal.fields.Name,
              updates: newUpdates
            })
          }
          for (const h of csvHeaders) {
            if (h.header === 'hire_date' || h.header === 'birthday') {
              const date1 = moment(newVal[h.header])
                            .format('YYYY-MM-DD')
              const date2 = moment(oldVal.fields[h.airtableColumn])
                            .format('YYYY-MM-DD')
              if (date1 !== date2) {
                newUpdates.push({
                  field: h.header,
                  newVal: date1,
                  oldVal: date2
                })
              }
            } else if (newVal[h.header] !== oldVal.fields[h.airtableColumn]) {
              if (oldVal.fields[h.airtableColumn] === undefined) {
                // do nothing if airtable is missing data
              } else {
                newUpdates.push({
                  field: h.airtableColumn,
                  newVal: newVal[h.header],
                  oldVal: oldVal.fields[h.airtableColumn]
                })
              }
            }
          }
          if (newUpdates.length === 0) {
            newUpdates.push({
              field: 'Status',
              newVal: 'New Hire',
              oldVal: null
            })
          }
          updates.push({
            name: newVal.user_name,
            updates: newUpdates
          })
        }
        function mapEmployeeFieldsForUpdate (obj, id) {
          const newObj = {id: id, fields: {}}
          for (const h of csvHeaders) {
            newObj.fields[h.airtableColumn] = obj[h.header]
          }
          newObj.fields.Status = 'Active'
          return newObj
        }
        function mapEmployeeFieldsForCreate (obj) {
          const newObj = {fields: {}}
          for (const h of csvHeaders) {
            newObj.fields[h.airtableColumn] = obj[h.header]
          }
          newObj.fields.Status = 'Active'
          return newObj
        }
      },
    }
  }
</script>
